import {
  FormControl,
  FormHelperText,
  FormLabel,
  RadioGroup,
  type RadioGroupProps,
} from '@mui/material';
import { forwardRef } from 'react';

import { Flex, type FlexProps } from '../flex';
import { Radio } from '../Inputs';
import { Text } from '../text';
import { mergeSx } from '../utils';

interface Option {
  label: string;
  value: string;
  icon: React.ReactNode;
  disabled?: boolean;
  'data-testid'?: string;
}

export interface RadioWithIconSelectionProps
  extends Pick<RadioGroupProps, 'value' | 'onChange' | 'defaultValue' | 'row' | 'sx' | 'name'> {
  label?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  options: Array<Option>;
  slotProps?: {
    optionWrapper?: FlexProps;
  };
}

const RadioWithIconSelection = forwardRef((props: RadioWithIconSelectionProps, ref) => {
  const {
    name,
    value,
    defaultValue,
    label,
    onChange,
    helperText,
    options,
    row: hasRow,
    sx,
    slotProps,
    ...restProps
  } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    onChange?.(event, value);
  };

  const { optionWrapper } = slotProps ?? {};
  const { sx: optionWrapperSx, ...optionWrapperRestProps } = optionWrapper ?? {};

  return (
    <FormControl
      {...restProps}
      sx={{
        gap: (theme) => theme.spacing(2),
        ...sx,
      }}
    >
      {label ? <FormLabel>{label}</FormLabel> : null}
      <RadioGroup
        name={name}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        row={hasRow}
        ref={ref}
        sx={(theme) => ({
          gap: theme.spacing(3),
        })}
      >
        {options.map(({ value: optionValue, label, icon, disabled, 'data-testid': dataTestId }) => {
          const isSelected = value === optionValue;
          const isDisabled = disabled || props.disabled;

          return (
            <Flex
              key={optionValue}
              data-testid={dataTestId}
              sx={mergeSx(
                (theme) => ({
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  border: '1px solid',
                  p: theme.spacing(3, 4),
                  borderRadius: theme.shape.rounded(1.5),
                  borderColor:
                    isSelected && !isDisabled
                      ? theme.palette.primary.main
                      : theme.palette.grey[200],
                }),
                optionWrapperSx,
              )}
              {...optionWrapperRestProps}
            >
              <Radio
                value={optionValue}
                label={
                  <Flex
                    sx={(theme) => ({
                      gap: theme.spacing(2),
                      alignItems: 'center',
                      color: isDisabled ? theme.palette.grey[400] : theme.palette.grey[600],
                    })}
                  >
                    {icon}
                    <Text
                      sx={(theme) => ({
                        color: isDisabled ? theme.palette.grey[400] : theme.palette.text.primary,
                      })}
                    >
                      {label}
                    </Text>
                  </Flex>
                }
                disabled={disabled}
                labelPlacement='start'
                sx={(theme) => ({
                  m: theme.spacing(0),
                  gap: theme.spacing(4),
                  flex: 1,
                })}
                radioProps={{
                  id: optionValue,
                  sx: (theme) => ({
                    p: theme.spacing(0),
                    color: isDisabled ? theme.palette.grey[400] : undefined,
                  }),
                }}
                slotProps={{
                  typography: {
                    sx: {
                      width: '100%',
                    },
                  },
                }}
              />
            </Flex>
          );
        })}
      </RadioGroup>
      {helperText ? (
        <FormHelperText sx={{ mt: !hasRow ? 1.5 : 0 }}>{helperText}</FormHelperText>
      ) : null}
    </FormControl>
  );
});
RadioWithIconSelection.displayName = 'RadioWithIconSelection';

export default RadioWithIconSelection;
